export function initHeaderOverlayMenu(root = document) {
  const overlay = root.querySelector('[data-header-overlay]');
  const triggers = Array.from(root.querySelectorAll('[data-header-overlay-trigger]'));

  if (!overlay || !triggers.length) {
    return;
  }

  const panel = overlay.querySelector('[data-header-overlay-panel]');
  const closeButtons = Array.from(root.querySelectorAll('[data-header-overlay-close]'));
  const menus = Array.from(overlay.querySelectorAll('[data-header-overlay-menu]'));
  const columnTriggers = Array.from(overlay.querySelectorAll('[data-header-overlay-column-trigger]'));
  const overlayLinks = Array.from(overlay.querySelectorAll('.header-overlay-menu__nav a'));
  const closeDelay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 320;
  const imageAnimations = menus
    .map((menu) => {
      const body = menu.querySelector('.header-overlay-menu__body');
      const imageWrap = menu.querySelector('.header-overlay-menu__image-wrap');
      const image = imageWrap?.querySelector('img');

      if (!(body instanceof HTMLElement) || !(imageWrap instanceof HTMLElement) || !(image instanceof HTMLImageElement)) {
        return null;
      }

      return { body, imageWrap, image };
    })
    .filter(Boolean);

  let activeLocation = null;
  let allowActiveFallback = true;
  let hoverLocation = null;
  let focusedTrigger = null;
  let closeTimeoutId = null;

  function syncImageAnimationMetrics() {
    imageAnimations.forEach((item) => {
      const { body, imageWrap, image } = item;

      if (!image.naturalWidth || !image.naturalHeight) {
        return;
      }

      const bodyHeight = body.clientHeight;
      const wrapWidth = imageWrap.clientWidth;

      if (!bodyHeight || !wrapWidth) {
        return;
      }

      const targetHeight = Math.min(bodyHeight, wrapWidth * (image.naturalHeight / image.naturalWidth));

      imageWrap.style.setProperty('--header-overlay-image-start-height', `${bodyHeight}px`);
      imageWrap.style.setProperty('--header-overlay-image-target-height', `${targetHeight}px`);
    });
  }

  function isOverlayOpen() {
    return overlay.classList.contains('is-open') && !overlay.hidden;
  }

  function visibleLocation() {
    return hoverLocation || (allowActiveFallback ? activeLocation : null);
  }

  function syncState() {
    const currentLocation = visibleLocation();

    triggers.forEach((trigger) => {
      const isExpanded = isOverlayOpen() && trigger.dataset.menuLocation === activeLocation;
      trigger.setAttribute('aria-expanded', String(isExpanded));
      trigger.classList.toggle('is-active', isExpanded);
    });

    menus.forEach((menu) => {
      const isOpen = isOverlayOpen() && menu.dataset.menuLocation === currentLocation;
      const title = menu.querySelector('[data-header-overlay-column-trigger]');
      const content = menu.querySelector('.header-overlay-menu__content');

      menu.classList.toggle('is-open', isOpen);
      menu.classList.toggle('is-closed', !isOpen);
      menu.dataset.state = isOpen ? 'open' : 'closed';

      if (isOpen) {
        menu.classList.add('was-open');
      }

      if (title) {
        title.setAttribute('aria-expanded', String(isOpen));
      }

      if (content) {
        content.setAttribute('aria-hidden', String(!isOpen));
      }
    });
  }

  function openOverlay(location, trigger = null) {
    if (!location) {
      return;
    }

    if (closeTimeoutId) {
      window.clearTimeout(closeTimeoutId);
      closeTimeoutId = null;
    }

    activeLocation = location;
    allowActiveFallback = true;
    hoverLocation = null;
    focusedTrigger = trigger || triggers.find((item) => item.dataset.menuLocation === location) || focusedTrigger;

    if (overlay.hidden) {
      overlay.hidden = false;
      menus.forEach((menu) => {
        menu.classList.remove('was-open');
      });
    }

    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('header-overlay-open');

    requestAnimationFrame(() => {
      syncImageAnimationMetrics();
      overlay.classList.add('is-open');
      syncState();
    });

    const closeButton = closeButtons[0];
    if (closeButton) {
      window.setTimeout(() => closeButton.focus(), 20);
    }
  }

  function finishClose(restoreFocus = true) {
    overlay.hidden = true;
    overlay.setAttribute('aria-hidden', 'true');

    if (restoreFocus && focusedTrigger) {
      focusedTrigger.focus();
    }

    activeLocation = null;
    hoverLocation = null;
    focusedTrigger = null;

    menus.forEach((menu) => {
      menu.classList.remove('was-open');
    });

    syncState();
  }

  function closeOverlay({ restoreFocus = true } = {}) {

    const mobileOverlay = document.getElementById('header-mobile-menu-overlay');

    if (
      mobileOverlay &&
      !mobileOverlay.hidden &&
      mobileOverlay.classList.contains('is-open')
    ) {
      mobileOverlay.classList.remove('is-open');
      document.body.classList.remove('header-overlay-open');
      mobileOverlay.setAttribute('aria-hidden', 'true');

      window.setTimeout(() => {
        mobileOverlay.hidden = true;
      }, closeDelay);

      return;
    }


    if (!isOverlayOpen() && overlay.hidden) {
      finishClose(restoreFocus);
      return;
    }

    overlay.classList.remove('is-open');
    document.body.classList.remove('header-overlay-open');
    overlay.setAttribute('aria-hidden', 'true');
    hoverLocation = null;
    syncState();

    if (closeTimeoutId) {
      window.clearTimeout(closeTimeoutId);
    }

    closeTimeoutId = window.setTimeout(() => {
      finishClose(restoreFocus);
      closeTimeoutId = null;
    }, closeDelay);
  }

  function setHoveredLocation(location = null) {
    hoverLocation = location;
    if (location !== null) {
      allowActiveFallback = false;
    }
    syncState();
  }

  triggers.forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      event.preventDefault();

      // ONLY the hamburger mobile button
      if (trigger.dataset.menuLocation === 'mobile') {
        const mobileOverlay = document.getElementById('header-mobile-menu-overlay');

        if (mobileOverlay) {
          mobileOverlay.hidden = false;
          mobileOverlay.setAttribute('aria-hidden', 'false');
          document.body.classList.add('header-overlay-open');

          requestAnimationFrame(() => {
            mobileOverlay.classList.add('is-open');
          });
        }

        return;
      }

      // everything else stays original
      openOverlay(trigger.dataset.menuLocation, trigger);
    });
  });

  columnTriggers.forEach((columnTrigger) => {
    const activateColumn = () => {
      activeLocation = columnTrigger.dataset.menuLocation || activeLocation;
      allowActiveFallback = true;
      hoverLocation = null;
      syncState();
    };

    columnTrigger.addEventListener('click', activateColumn);

    // Support keyboard activation for non-button elements (span)
    columnTrigger.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        activateColumn();
      }
    });
  });

  menus.forEach((menu) => {
    const { menuLocation } = menu.dataset;

    menu.addEventListener('mouseenter', () => {
      if (!isOverlayOpen()) {
        return;
      }

      setHoveredLocation(menuLocation);
    });

    menu.addEventListener('mouseleave', () => {
      if (!isOverlayOpen()) {
        return;
      }

      setHoveredLocation(null);
    });

    menu.addEventListener('focusin', () => {
      if (!isOverlayOpen()) {
        return;
      }

      setHoveredLocation(menuLocation);
    });

    menu.addEventListener('focusout', () => {
      if (!isOverlayOpen()) {
        return;
      }

      window.requestAnimationFrame(() => {
        if (!menu.contains(document.activeElement)) {
          setHoveredLocation(null);
        }
      });
    });
  });

  closeButtons.forEach((button) => {
    button.addEventListener('click', () => {
      closeOverlay();
    });
  });

  overlay.addEventListener('click', (event) => {
    if (event.target === overlay || event.target === panel?.parentElement) {
      closeOverlay();
    }
  });

  overlayLinks.forEach((link) => {
    link.addEventListener('click', () => {
      closeOverlay({ restoreFocus: false });
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isOverlayOpen()) {
      closeOverlay();
    }
  });

  imageAnimations.forEach(({ image }) => {
    if (!image.complete) {
      image.addEventListener('load', syncImageAnimationMetrics, { once: true });
    }
  });

  if ('ResizeObserver' in window) {
    const resizeObserver = new ResizeObserver(() => {
      if (!overlay.hidden) {
        syncImageAnimationMetrics();
      }
    });

    resizeObserver.observe(overlay);
  } else {
    window.addEventListener('resize', () => {
      if (!overlay.hidden) {
        syncImageAnimationMetrics();
      }
    });
  }

  syncState();
}
